import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { timeAgo } from "@/lib/api";
import { sectorTrack } from "./SectorHero";

export const REG_STATUS: Record<string, { label: string; cls: string }> = {
  enforced: { label: "ENFORCED", cls: "border-[#34D399]/40 bg-[#34D399]/10 text-[#34D399]" },
  enacted: { label: "ENACTED", cls: "border-[#00E5FF]/40 bg-[#00E5FF]/10 text-[#00E5FF]" },
  in_progress: { label: "IN PROGRESS", cls: "border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA]" },
  proposed: { label: "PROPOSED", cls: "border-[#60A5FA]/40 bg-[#60A5FA]/10 text-[#60A5FA]" },
  rejected: { label: "REJECTED", cls: "border-[#F87171]/40 bg-[#F87171]/10 text-[#F87171]" },
};

export const StatusChip = ({ status }: { status: string }) => {
  const s = REG_STATUS[status] || { label: (status || "").replace("_", " ").toUpperCase(), cls: "border-white/20 bg-white/[0.06] text-gray-300" };
  return (
    <span className={`inline-flex rounded border px-1.5 py-0.5 font-mono text-[8.5px] font-bold tracking-[0.14em] ${s.cls}`}>
      {s.label}
    </span>
  );
};

/* Canonical regulatory lifecycle — exactly the statuses the Regulation Tracker
   stores. Positions reflect CURRENT status only; no historical transitions are
   drawn or animated (status history is recorded as it is observed). */
const LIFECYCLE = [
  { key: "proposed", label: "PROPOSED", color: "#60A5FA", desc: "Drafted or in consultation" },
  { key: "in_progress", label: "IN PROGRESS", color: "#A78BFA", desc: "Active rulemaking or review" },
  { key: "enacted", label: "ENACTED", color: "#00E5FF", desc: "Adopted into law" },
  { key: "enforced", label: "ENFORCED", color: "#34D399", desc: "In force and applied" },
];
const OFF_PATH = { key: "rejected", label: "REJECTED", color: "#F87171", desc: "Withdrawn or struck down" };
const JCODE: Record<string, string> = { "United States": "US", "European Union": "EU", "United Kingdom": "UK" };

const jurisOf = (r: any) =>
  (r.countries || []).map((c: string) => JCODE[c] || c.slice(0, 2).toUpperCase()).join("·");

const ariaOf = (r: any) =>
  `${r.title}. ${(r.countries || []).join(", ")}. Status ${String(r.status || "").replace("_", " ")}. Updated ${timeAgo(r.updated_at)}. ${r.linked_articles || 0} linked ${r.linked_articles === 1 ? "story" : "stories"}.`;

const isRecent = (r: any) => r.updated_at && Date.now() - new Date(r.updated_at).getTime() < 7 * 864e5;

const RegNode = ({ r }: { r: any }) => {
  const coverage = (r.linked_items || (r.latest_linked ? [r.latest_linked] : [])).slice(0, 2);
  return (
    <div className="relative z-[1]">
      <Link to={`/regulation/${r.slug}`} aria-label={ariaOf(r)} data-testid={`radar-reg-${r.slug}`}
        onClick={() => sectorTrack("regulation_click", r.slug, "policy")}
        className="group block rounded-lg border border-white/[0.09] bg-white/[0.035] backdrop-blur-[2px] px-3 py-2.5 hover:border-white/[0.26] hover:bg-white/[0.06] focus-visible:border-[#A78BFA]/60 outline-none transition-colors">
        <div className="flex items-center gap-2 min-w-0">
          <span className="rounded border border-white/[0.16] bg-black/40 px-1.5 py-0.5 font-mono text-[8.5px] font-bold tracking-[0.14em] text-gray-200 shrink-0">
            {jurisOf(r)}
          </span>
          <span className="font-mono text-[8.5px] uppercase tracking-wide text-gray-500 truncate">
            {timeAgo(r.updated_at)}
          </span>
          {isRecent(r) && (
            <span className="nl-radar-recent rounded border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-1.5 py-0.5 font-mono text-[7.5px] font-bold tracking-[0.16em] text-[#00E5FF] shrink-0">
              UPDATED
            </span>
          )}
        </div>
        <p className="text-white font-semibold text-[12.5px] leading-snug mt-1.5 line-clamp-2 group-hover:text-[#A78BFA] transition-colors">
          {r.title}
        </p>
        {r.linked_articles > 0 && (
          <p className="font-mono text-[8.5px] uppercase tracking-wide mt-1.5 truncate"
            title="Confirmed article ↔ regulation links only — candidates awaiting review are not counted">
            <span className="text-[#00E5FF]">{r.linked_articles} linked {r.linked_articles === 1 ? "story" : "stories"}</span>
          </p>
        )}
      </Link>
      {/* Confirmed linked coverage only — records with zero links render no coverage container */}
      {coverage.map((c: any) => (
        <Link key={c.slug} to={`/article/${c.slug}`}
          onClick={() => sectorTrack("regulation_latest_linked_click", c.slug, "policy")}
          className="group flex items-baseline gap-1.5 mt-1.5 pl-1 min-w-0">
          <span aria-hidden className="font-mono text-[9px] text-[#00E5FF] shrink-0">↳</span>
          <span className="text-gray-400 text-[11px] leading-snug truncate group-hover:text-gray-200 transition-colors">
            {c.title}
          </span>
        </Link>
      ))}
    </div>
  );
};

/* TRACKED NOW — exact-data sidecar: the visualization is never the sole carrier */
const TrackedNow = ({ activity, className = "" }: { activity: any[]; className?: string }) => (
  <div data-testid="radar-tracked-now"
    className={`rounded-xl border border-white/[0.08] bg-black/40 backdrop-blur-md p-4 ${className}`}>
    <p className="font-mono text-[9.5px] font-bold uppercase tracking-[0.26em] text-[#00E5FF]">Tracked Now</p>
    <p className="font-mono text-[8px] uppercase tracking-wide text-gray-600 mt-1 mb-2.5">
      Production regulation records · {activity.length} tracked
    </p>
    <div className="divide-y divide-white/[0.06]">
      {activity.map((r) => (
        <Link key={r.id} to={`/regulation/${r.slug}`} data-testid={`tracked-now-${r.slug}`}
          aria-label={ariaOf(r)}
          onClick={() => sectorTrack("regulation_click", r.slug, "policy")}
          className="group block py-2 first:pt-0 last:pb-0">
          <div className="flex items-center gap-2">
            <StatusChip status={r.status} />
            <span className="font-mono text-[8.5px] font-bold tracking-wide text-gray-400">{jurisOf(r)}</span>
            <span className="ml-auto font-mono text-[8.5px] text-gray-600">{timeAgo(r.updated_at)}</span>
          </div>
          <p className="text-gray-200 font-semibold text-[12.5px] leading-snug mt-1 line-clamp-1 group-hover:text-[#A78BFA] transition-colors">
            {r.title}
          </p>
          {r.linked_articles > 0 && (
            <p className="font-mono text-[8.5px] uppercase tracking-wide mt-0.5">
              <span className="text-[#00E5FF]">{r.linked_articles} linked {r.linked_articles === 1 ? "story" : "stories"}</span>
            </p>
          )}
        </Link>
      ))}
    </div>
  </div>
);

/* REGULATION RADAR — Policy's signature intelligence band. Naming: this live
   structured module is "Regulation Radar · LIVE TRACKER"; the premium weekly
   email digest keeps its separate "Regulation Radar" briefing identity. */
export const RegulationRadarBand = ({ activity }: { activity: any[] }) => {
  if (!activity.length) return null;
  const byStatus: Record<string, any[]> = {};
  for (const r of activity) (byStatus[r.status] = byStatus[r.status] || []).push(r);
  const stages = [...LIFECYCLE, ...(byStatus.rejected?.length ? [OFF_PATH] : [])];
  return (
    <section data-testid="policy-regulation-radar"
      className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-8">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1.5">
        <div className="flex items-center gap-2.5">
          <span aria-hidden className="h-3.5 w-[3px] rounded-full bg-[#A78BFA]" />
          <h2 className="font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-[#A78BFA]">Regulation Radar</h2>
          <span data-testid="radar-live-tracker-chip"
            className="rounded border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-1.5 py-0.5 font-mono text-[8px] font-bold tracking-[0.18em] text-[#00E5FF]">
            LIVE TRACKER
          </span>
        </div>
        <Link to="/regulation-tracker" data-testid="radar-full-tracker-link"
          onClick={() => sectorTrack("regulation_tracker_click", "radar", "policy")}
          className="group inline-flex items-center gap-1 font-mono text-[9.5px] uppercase tracking-wide text-gray-400 hover:text-[#00E5FF] transition-colors">
          Full tracker
          <ArrowRight className="h-3 w-3 transition-transform duration-200 group-hover:translate-x-0.5" />
        </Link>
      </div>
      <p className="text-gray-500 text-[13px] mb-4">
        Every regulation tracked by the NewsLayer desk, positioned at its current regulatory-lifecycle status.
      </p>

      <div className="grid lg:grid-cols-[minmax(0,1fr)_330px] xl:grid-cols-[minmax(0,1fr)_360px] gap-4 items-stretch">
        {/* Lifecycle field — desktop */}
        <div className="hidden lg:flex flex-col min-w-0 relative rounded-xl border border-white/[0.08] bg-[#0a0b10] p-5 overflow-hidden"
          data-testid="radar-lifecycle-field">
          <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none"
            style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "46px 46px" }} />
          <div aria-hidden className="absolute -top-20 left-1/3 h-48 w-96 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(closest-side, rgba(124,58,237,0.12), transparent)" }} />
          <div className="relative">
            <div aria-hidden className="nl-radar-spine-x absolute top-[7px] h-[2px] rounded-full"
              style={{ left: `${100 / (stages.length * 2)}%`, right: `${100 / (stages.length * 2)}%`, background: "linear-gradient(to right, #60A5FA, #A78BFA 38%, #00E5FF 70%, #34D399)" }} />
            <div className={`grid gap-3 xl:gap-4 ${stages.length === 5 ? "grid-cols-5" : "grid-cols-4"}`}>
              {stages.map((st) => {
                const regs = byStatus[st.key] || [];
                return (
                  <div key={st.key} className="flex flex-col" data-testid={`radar-stage-${st.key.replace("_", "-")}`}>
                    <div className="flex flex-col items-center text-center">
                      <span aria-hidden className="relative z-[1] h-4 w-4 rounded-full border-2 grid place-items-center bg-[#0a0b10]"
                        style={{ borderColor: st.color, boxShadow: regs.length ? `0 0 12px ${st.color}66` : "none" }}>
                        {regs.length > 0 && <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: st.color }} />}
                      </span>
                      <p className="font-mono text-[9px] font-bold tracking-[0.22em] mt-2" style={{ color: st.color }}>{st.label}</p>
                      <p className="font-mono text-[7.5px] uppercase tracking-wide text-gray-600 mt-0.5">{st.desc}</p>
                    </div>
                    <div className="mt-2.5 space-y-2 flex-1">
                      {regs.length ? regs.map((r) => <RegNode key={r.id} r={r} />) : (
                        <p className="font-mono text-[8px] uppercase tracking-wide text-gray-700 text-center pt-2">
                          No tracked records
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <p className="relative font-mono text-[8px] uppercase tracking-[0.14em] text-gray-600 mt-4 pt-3 border-t border-white/[0.05] leading-relaxed">
            Positions reflect current status only — lifecycle transitions are recorded as they are observed
          </p>
        </div>

        {/* Vertical lifecycle rail — below lg (same facts, mobile-first geometry) */}
        <div className="lg:hidden min-w-0 overflow-hidden rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4"
          data-testid="radar-lifecycle-mobile">
          <div className="relative pl-7">
            <div aria-hidden className="nl-radar-spine absolute left-[9px] top-1 bottom-1 w-[2px] rounded-full"
              style={{ background: "linear-gradient(to bottom, #60A5FA, #A78BFA 38%, #00E5FF 70%, #34D399)" }} />
            {stages.map((st) => {
              const regs = byStatus[st.key] || [];
              return (
                <div key={st.key} className="relative pb-4 last:pb-0">
                  <span aria-hidden className="absolute -left-[23px] top-[2px] grid h-[14px] w-[14px] place-items-center">
                    <span className="h-2.5 w-2.5 rounded-full border-2"
                      style={{ borderColor: st.color, backgroundColor: regs.length ? `${st.color}40` : "transparent", boxShadow: regs.length ? `0 0 9px ${st.color}66` : "none" }} />
                  </span>
                  <div className="flex items-baseline gap-2 min-w-0">
                    <p className="font-mono text-[9px] font-bold tracking-[0.22em]" style={{ color: st.color }}>{st.label}</p>
                    <p className="font-mono text-[8px] uppercase tracking-wide text-gray-600 truncate">{st.desc}</p>
                  </div>
                  <div className="mt-2 space-y-2">
                    {regs.length ? regs.map((r) => <RegNode key={r.id} r={r} />) : (
                      <p className="font-mono text-[8.5px] uppercase tracking-wide text-gray-600/80">
                        No tracked records at this stage
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="font-mono text-[8px] uppercase tracking-[0.14em] text-gray-600 mt-4 leading-relaxed">
            Positions reflect current status only — transitions are recorded as they are observed
          </p>
        </div>

        <TrackedNow activity={activity} className="hidden lg:block" />
      </div>
    </section>
  );
};
